/**
 * Utility functions for validating ingredients
 */

/**
 * List of banned substances that should not be allowed in the application
 * This includes illegal drugs, toxic/poisonous substances, and non-food items
 * 
 * We use exact matches for common items that could be part of valid food names,
 * and include prefixes/suffixes for better matching where necessary.
 */
export const BANNED_SUBSTANCES = [
  // Illegal drugs
  'marijuana', 'cannabis', 'cocaine', 'heroin', 'methamphetamine', 'ecstasy', 'mdma', 'lsd', 'pcp',
  'ketamine', 'ghb', 'flakka', 'bath salts', 'spice', 'k2', 'kratom', 'khat', 'peyote', 
  'magic mushroom', 'magic mushrooms', 'psychedelic mushroom', 'psychedelic mushrooms', 
  'psychedelic', 'amphetamine', 'opium', 'fentanyl',
  
  // Harmful chemicals and toxins
  'bleach', 'ammonia', 'pesticide', 'insecticide', 'arsenic', 'cyanide', 'mercury',
  'lead', 'chlorine', 'formaldehyde', 'asbestos', 'antifreeze', 'detergent', 'acid',
  'poison', 'toxic', 'rat poison', 'strychnine',
  
  // Lethal plants and substances
  'hemlock', 'nightshade', 'belladonna', 'oleander', 'ricin', 'castor beans',
  'foxglove', 'death cap mushroom', 'death cap', 'destroying angel mushroom', 'destroying angel', 
  'aconite', 'wolfsbane', 'jimsonweed',
  'tobacco', 'cigarette',
  
  // Non-food substances
  'gasoline', 'kerosene', 'motor oil', 'paint', 'paint thinner', 'glue',
  'solvent', 'cleaning fluid', 'lighter fluid', 'poo', 'poop'
];

/**
 * Special cases requiring exact matching, partial words, or more complex logic
 */
export const SPECIAL_CHECKS = [
  {
    // Check for dangerous mushroom combinations
    pattern: (input: string) => {
      const lowerInput = input.toLowerCase();
      
      // Match "magic mushrooms" variations more precisely
      if (lowerInput.includes('magic')) {
        // Only flag if "magic" and variants of "mushrooms" are close to each other
        // in a way that suggests illicit substances
        if (
          // Common magic mushroom phrases
          lowerInput.includes('magic mushroom') || 
          lowerInput.includes('magic shroom') ||
          
          // Patterns where magic directly modifies mushroom with optional words between
          /\bmagic\s+\w{0,5}\s*mushrooms?\b/i.test(lowerInput) ||
          /\bmushrooms?\s+\w{0,5}\s*magic\b/i.test(lowerInput)
        ) {
          // Exclude common culinary/flavor descriptors
          const culinaryExceptions = [
            'flavor', 'taste', 'seasoning', 'spice', 'herb', 
            'recipe', 'dish', 'meal', 'sauce', 'dressing'
          ];
          
          // If it's a culinary context, don't flag it
          if (culinaryExceptions.some(term => lowerInput.includes(term))) {
            return false;
          }
          
          return true;
        }
      }
      
      // Match specific poisonous mushroom names
      const poisonousMushrooms = ['death cap', 'destroying angel', 'amanita phalloides'];
      return poisonousMushrooms.some(type => lowerInput.includes(type));
    },
    reason: 'Contains banned or poisonous mushroom variety'
  },
  
  {
    // Check for illicit drug references in context
    pattern: (input: string) => {
      const lowerInput = input.toLowerCase();
      const words = lowerInput.split(/\s+/);
      
      // Look for drug-related terms as full words only
      const drugTerms = ['cocaine', 'heroin', 'meth', 'marijuana', 'cannabis', 'weed'];
      return drugTerms.some(term => words.includes(term));
    },
    reason: 'Contains banned drug substance'
  }
];

/**
 * Checks if an ingredient contains any banned substances
 * @param ingredient The ingredient to check
 * @returns An object with validation result and reason if banned
 */
export const validateIngredient = (ingredient: string): { valid: boolean; reason?: string } => {
  // Return valid for empty inputs
  if (!ingredient || !ingredient.trim()) {
    return { valid: true };
  }
  
  // Convert to lowercase for case-insensitive matching
  const lowerIngredient = ingredient.toLowerCase().trim();
  
  // First check special cases requiring more complex logic
  for (const specialCheck of SPECIAL_CHECKS) {
    if (specialCheck.pattern(lowerIngredient)) {
      return {
        valid: false,
        reason: specialCheck.reason
      };
    }
  }
  
  // Check against the banned substances list using word boundaries when possible
  for (const substance of BANNED_SUBSTANCES) {
    // For exact matching, check if the substance appears as a whole word
    const substanceRegex = new RegExp(`\\b${substance}\\b`, 'i');
    
    // Match exact or close variations (start/end of word, etc.)
    if (
      substanceRegex.test(lowerIngredient) || 
      lowerIngredient === substance ||
      lowerIngredient.startsWith(`${substance} `) ||
      lowerIngredient.endsWith(` ${substance}`) ||
      lowerIngredient.includes(` ${substance} `)
    ) {
      return {
        valid: false,
        reason: `Contains banned substance: ${substance}`
      };
    }
  }
  
  return { valid: true };
};

/**
 * Utility function to filter a list of ingredients, removing any banned substances
 * @param ingredients List of ingredients to filter
 * @returns An object with filtered ingredients and removed substances
 */
export const filterIngredients = (ingredients: string[]): { 
  valid: string[]; 
  invalid: Array<{ ingredient: string; reason: string }> 
} => {
  const valid: string[] = [];
  const invalid: Array<{ ingredient: string; reason: string }> = [];
  
  for (const ingredient of ingredients) {
    const validation = validateIngredient(ingredient);
    if (validation.valid) {
      valid.push(ingredient);
    } else {
      invalid.push({
        ingredient,
        reason: validation.reason || 'Unknown reason'
      });
    }
  }
  
  return { valid, invalid };
}; 